import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { cx } from './cx';

export function Checklist({
  items,
  variant = 'check',
  columns = false,
  size = 'md',
  className,
}: {
  items: ReactNode[];
  variant?: 'check' | 'cross';
  columns?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}) {
  return (
    <ul
      className={cx(
        'checklist',
        variant === 'cross' && 'checklist--cross',
        columns && 'checklist--columns',
        size === 'sm' && 'checklist--sm',
        className
      )}
    >
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  );
}

export function Faq({ items, className }: { items: { question: ReactNode; answer: ReactNode }[]; className?: string }) {
  return (
    <div className={cx('faq', className)}>
      {items.map((item, index) => (
        <details key={index} className="faq__item">
          <summary className="faq__summary">{item.question}</summary>
          <div className="faq__body">{item.answer}</div>
        </details>
      ))}
    </div>
  );
}

export function CtaBlock({ title, text, children, className }: { title: ReactNode; text?: ReactNode; children?: ReactNode; className?: string }) {
  return (
    <div className={cx('cta-block', className)}>
      <h2>{title}</h2>
      {text && <p>{text}</p>}
      {children && <div className="btn-group btn-group--center">{children}</div>}
    </div>
  );
}

export function Alert({ tone = 'info', className, ...props }: ComponentPropsWithoutRef<'div'> & { tone?: 'info' | 'success' | 'error' }) {
  return <div role={tone === 'error' ? 'alert' : 'status'} className={cx('alert', `alert--${tone}`, className)} {...props} />;
}

export function EmptyState({ title, children, className }: { title: ReactNode; children?: ReactNode; className?: string }) {
  return (
    <div className={cx('empty-state', className)}>
      <p className="empty-state__title">{title}</p>
      {children}
    </div>
  );
}

/** Items are rendered in order with a separator; mark the last one with aria-current="page" */
export function Breadcrumb({ label, items, className }: { label: string; items: ReactNode[]; className?: string }) {
  return (
    <nav aria-label={label} className={cx('breadcrumb', className)}>
      <ol>
        {items.map((item, index) => (
          <li key={index}>{item}</li>
        ))}
      </ol>
    </nav>
  );
}

export function Price({ amount, note, className }: { amount: ReactNode; note?: ReactNode; className?: string }) {
  return (
    <div className={cx('price', className)}>
      <span className="price__amount">{amount}</span>
      {note && <span className="price__note">{note}</span>}
    </div>
  );
}
